import * as React from "react";
import { useState } from "react";

import {
  faCalendarDays,
  faFilter,
  faFilterSlash,
  faSearch,
} from "@fortawesome/sharp-regular-svg-icons";
import { library } from "@fortawesome/fontawesome-svg-core";
import {
  Autocomplete,
  Box,
  Button,
  focusRing,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid2 as Grid,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@platform/style";

import { TemplateV2 } from "../../../models/TemplateV2";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleInfo } from "@fortawesome/pro-solid-svg-icons";

library.add(faFilter, faCalendarDays, faFilterSlash, faSearch);

interface WorkTemplateConfigurationProps {
  onHandleBack: () => void;
  onHandleNext: () => void;
  onTemplateUpdate: any;
  selectedTemplate?: TemplateV2;
}

const INTEGRATED_SYSTEMS_KEY_FIELD_NAME = "IntegratedSystemKeys";
const CREATE_WORK_INTEGRATED_SYSTEM_NAME = "CreateWorkInIntegratedSystemIndicator";
const AVAILABLE_TO_USER_NAME = "AvailableToUserIndicator";
const INTEGRATED_SYSTEM_NAME = "IntegratedSystem";

export default function WorkTemplateConfiguration(props: Readonly<WorkTemplateConfigurationProps>) {
  const [template, setTemplate] = useState(props?.selectedTemplate);
  const integratedSystemOptions = ["NONE", "MSP-TASK-TRACKING"];
  const [selectedIntegratedSystem, setSelectedIntegratedSystem] = useState(
    template?.IntegratedSystem
  );
  const [integratedSystemValue, setIntegratedSystemValue] = useState(
    template?.IntegratedSystemKeys[0]?.Value ? template?.IntegratedSystemKeys[0]?.Value : ""
  );
  const [availableToUserIndicator, setAvailableToUserIndicator] = useState(
    template?.AvailableToUserIndicator
  );
  const [createWorkIndicator, setCreateWorkIndicator] = useState(
    template?.CreateWorkInIntegratedSystemIndicator
  );

  const min = 0;
  const max = 10;
  const integratedSystemKey = "MSP-Task-Header";
  // Compute error states based on the current values in the template
  const errorSLA = !template?.ServiceLevelInDays;
  const errorPriority = !template?.Priority;
  const isSystemMSPTaskTracking = selectedIntegratedSystem === integratedSystemOptions[1];
  const disableNext =
    (integratedSystemValue === "" && isSystemMSPTaskTracking) || errorSLA || errorPriority;

  const handleTemplateChange = (fieldName: string, value: any) => {
    if (fieldName == INTEGRATED_SYSTEMS_KEY_FIELD_NAME) {
      if (value == null) {
        value = [];
      } else {
        value = [{ Key: integratedSystemKey, Value: value }];
      }
    }

    if (template) {
      setTemplate({
        ...template,
        [fieldName]: value,
      });
    }
  };

  const handleNext = () => {
    props.onTemplateUpdate(template, selectedIntegratedSystem);
    props.onHandleNext();
  };

  function handleIntegrationSystemSelection(newValue: string | null) {
    if (newValue === null || newValue === integratedSystemOptions[0]) {
      handleTemplateChange(INTEGRATED_SYSTEM_NAME, integratedSystemOptions[0]);
      setCreateWorkIndicator(false);
      handleTemplateChange(CREATE_WORK_INTEGRATED_SYSTEM_NAME, false);
      handleTemplateChange(INTEGRATED_SYSTEMS_KEY_FIELD_NAME, null);
    } else {
      handleTemplateChange(INTEGRATED_SYSTEM_NAME, newValue);
    }
  }

  return (
    <>
      <Typography
        aria-label={"Template Configuration Header"}
        variant="h6"
        justifyContent="center"
        alignItems="center"
        sx={{ paddingTop: "20px", paddingBottom: "15px", paddingLeft: "3px" }}
      >
        Configure Work Template
      </Typography>
      <Paper
        elevation={0}
        variant="outlined"
        min-height="100vh"
        sx={{
          backgroundColor: "inherit",
          display: "flex",
          flexDirection: "column",
          border: "none",
          paddingLeft: "8px",
          paddingRight: "8px",
        }}
      >
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 5 }}>
            <Autocomplete
              id="business-area"
              options={[]}
              defaultValue={template?.BusinessArea}
              disabled
              renderInput={(params) => (
                <TextField {...params} label="Business Area" placeholder="Business Area" />
              )}
            />
          </Grid>
          <Grid size={{ xs: 5 }} sx={{ marginLeft: "auto" }}>
            <Autocomplete
              id="business-function"
              options={[]}
              defaultValue={template?.BusinessFunction}
              disabled
              renderInput={(params) => (
                <TextField {...params} label="Business Function" placeholder="Business Function" />
              )}
            />
          </Grid>
          <Grid size={{ xs: 5 }} sx={{ display: "grid" }}>
            <TextField
              label="Template Description"
              variant="outlined"
              id="templateDescription"
              defaultValue={template?.Description}
              disabled
              slotProps={{ input: { readOnly: true } }}
            />
          </Grid>
          <Grid size={{ xs: 5 }} sx={{ marginLeft: "auto" }}>
            <Autocomplete
              multiple={false}
              id="business-process"
              options={[]}
              defaultValue={template?.BusinessProcess}
              disabled
              renderInput={(params) => (
                <TextField {...params} label="Business Process" placeholder="Business Process" />
              )}
            />
          </Grid>
          <Grid size={{ xs: 5 }} sx={{ display: "grid" }}>
            <TextField
              label="Template Title"
              variant="outlined"
              id="templateTitle"
              value={template?.Title}
              disabled
              slotProps={{ input: { readOnly: true } }}
            />
          </Grid>
          <Grid size={{ xs: 2.6 }} sx={{ marginLeft: "auto" }}>
            <TextField
              required
              error={errorSLA}
              label={"SLA"}
              type="number"
              placeholder={"Enter SLA"}
              slotProps={{ htmlInput: { min } }}
              helperText={errorSLA ? "Required field." : "Number of days to complete"}
              value={template?.ServiceLevelInDays}
              onChange={(e) => {
                let value = parseInt(e.target.value, 10);
                if (value < min) value = min;
                handleTemplateChange("ServiceLevelInDays", value);
              }}
            />
          </Grid>
          <Grid size={{ xs: 2.4 }} sx={{ display: "grid" }}>
            <TextField
              error={errorPriority}
              required
              label={"Priority"}
              type="number"
              placeholder={"Enter Priority"}
              slotProps={{ htmlInput: { min, max } }}
              value={template?.Priority}
              helperText={errorPriority ? "Required field." : "Priority Level"}
              onChange={(e) => {
                let value = parseInt(e.target.value, 10);
                if (value > max) value = max;
                if (value < min) value = min;
                handleTemplateChange("Priority", value);
              }}
            />
          </Grid>
          <Grid size={{ xs: 7 }} sx={{ display: "grid" }}>
            <Typography sx={{ fontWeight: "bold" }}>Template Settings</Typography>
          </Grid>
          <Grid size={{ xs: 5 }} sx={{ display: "grid" }}></Grid>
          <Grid size={{ xs: 5 }} sx={{ display: "grid" }}>
            <Stack
              direction="row"
              sx={{
                alignItems: "center",
                display: "flex",
              }}
            >
              <FormControl>
                <FormLabel
                  sx={{
                    paddingRight: "12px",
                  }}
                  id="radio-buttons-group-label"
                >
                  Allow user to create work from template.
                </FormLabel>
                <RadioGroup
                  aria-labelledby="radio-buttons-group-label"
                  name="radio-buttons-group"
                  value={availableToUserIndicator}
                  onChange={(e) => {
                    const selectedIndicator = e.target.value == "true";
                    setAvailableToUserIndicator(selectedIndicator);
                    handleTemplateChange(AVAILABLE_TO_USER_NAME, selectedIndicator);
                  }}
                  sx={{
                    display: "unset",
                  }}
                >
                  <FormControlLabel value="true" control={<Radio />} label="Yes" />
                  <FormControlLabel value="false" control={<Radio />} label="No" />
                </RadioGroup>
              </FormControl>

              <TextField
                label="Template Code"
                variant="outlined"
                id="templateCode"
                defaultValue={template?.TemplateCode}
                helperText={!integratedSystemValue && isSystemMSPTaskTracking ? " " : ""}
                disabled
                slotProps={{ input: { readOnly: true } }}
              />
            </Stack>
          </Grid>
          <Grid size={{ xs: 2.5 }} sx={{ display: "grid", marginLeft: "auto" }}>
            <Autocomplete
              id="integrated-system"
              options={integratedSystemOptions}
              value={selectedIntegratedSystem}
              onChange={(event, newValue) => {
                setSelectedIntegratedSystem(newValue ?? integratedSystemOptions[0]);
                handleIntegrationSystemSelection(newValue);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Integrated System"
                  placeholder="Select an Integrated System"
                  helperText={!integratedSystemValue && isSystemMSPTaskTracking ? " " : ""}
                />
              )}
            />
          </Grid>
          <Grid size={{ xs: 2.5 }} sx={{ display: "grid" }}>
            <TextField
              error={!integratedSystemValue && isSystemMSPTaskTracking}
              required={isSystemMSPTaskTracking}
              label="Integrated System Value"
              variant="outlined"
              id="integrated-system-value"
              value={!isSystemMSPTaskTracking ? "" : integratedSystemValue}
              helperText={
                !integratedSystemValue && isSystemMSPTaskTracking ? "Required field." : ""
              }
              onChange={(e) => {
                setIntegratedSystemValue(e.target.value);
              }}
              onBlur={() => {
                handleTemplateChange(INTEGRATED_SYSTEMS_KEY_FIELD_NAME, integratedSystemValue);
              }}
              sx={{
                display: !isSystemMSPTaskTracking ? "none" : "inline-flex",
              }}
            />
          </Grid>
          <Grid container sx={{ mt: 2, justifyContent: "flex-end" }}>
            <Stack
              direction="row"
              sx={{
                alignItems: "center",
                display: !isSystemMSPTaskTracking ? "none" : "flex",
              }}
            >
              <Typography>Create work in integrated system</Typography>
              &nbsp;
              <Tooltip title="Turn on integrated system to link template to selected system.">
                <FontAwesomeIcon
                  data-testid={"integrated-work-info-icon"}
                  icon={faCircleInfo}
                  color={"#235f73"}
                />
              </Tooltip>
              <Switch
                color="primary"
                aria-label={"Create Work Toggle"}
                checked={createWorkIndicator}
                onChange={(e) => {
                  setCreateWorkIndicator(!isSystemMSPTaskTracking ? false : e.target.checked);
                  handleTemplateChange(CREATE_WORK_INTEGRATED_SYSTEM_NAME, e.target.checked);
                }}
              />
            </Stack>
          </Grid>
          <Grid size={{ xs: 2.5 }} sx={{ display: "grid", marginLeft: "auto" }}>
            <Box
              sx={{
                bottom: "60px",
                display: "flex",
                flexDirection: "row",
                pt: 2,
                justifyContent: "flex-end",
              }}
            >
              <Button
                aria-label="Template Config Back"
                variant="outlined"
                color={"primary"}
                sx={{ focusRing, marginRight: "5px" }}
                onClick={props.onHandleBack}
              >
                Back
              </Button>
              <Button
                aria-label="Template Config Next"
                variant="contained"
                color={"primary"}
                sx={focusRing}
                disabled={disableNext}
                onClick={handleNext}
              >
                Next
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>
    </>
  );
}
